import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ViewStyle,
  TextStyle,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

export interface ChatInputBarProps {
  /** 杈撳叆妗嗙殑鍊?*/
  value: string;
  /** 杈撳叆妗嗗€煎彉鍖栧洖璋?*/
  onChangeText: (text: string) => void;
  /** 鍙戦€佹秷鎭洖璋?*/
  onSend: () => void;
  /** 鍗犱綅绗︽枃鏈?*/
  placeholder?: string;
  /** 鏄惁姝ｅ湪鍙戦€?*/
  sending?: boolean;
  /** 鏄惁绂佺敤杈撳叆 */
  disabled?: boolean;
  /** 鏈€澶у瓧绗﹂暱搴?*/
  maxLength?: number;
  /** 涓婚棰滆壊 */
  themeColor?: string;
  /** 瀹瑰櫒鏍峰紡 */
  containerStyle?: ViewStyle;
  /** 杈撳叆妗嗘牱寮?*/
  inputStyle?: TextStyle;
  /** 鍙戦€佹寜閽牱寮?*/
  sendButtonStyle?: ViewStyle;
  /** 提及按鈕回呼 (可選) */
  onMentionPress?: () => void;
  /** 表情按鈕回呼 (可選) */
  onEmojiPress?: () => void;
}

/**
 * 鑱婂ぉ杈撳叆鏍忕粍浠? * 
 * 鍖呭惈杈撳叆妗嗗拰鍙戦€佹寜閽紝鏀寔澶氳杈撳叆
 * 
 * @example
 * ```tsx
 * <ChatInputBar
 *   value={message}
 *   onChangeText={setMessage}
 *   onSend={handleSend}
 *   placeholder="杈撳叆娑堟伅..."
 *   sending={isSending}
 *   themeColor="#4A90E2"
 * />
 * ```
 */
export const ChatInputBar: React.FC<ChatInputBarProps> = ({
  value,
  onChangeText,
  onSend,
  placeholder = '杈撳叆娑堟伅...',
  sending = false,
  disabled = false,
  maxLength = 500,
  themeColor = '#4A90E2',
  containerStyle,
  inputStyle,
  sendButtonStyle,
  onMentionPress,
  onEmojiPress,
}) => {
  const inputRef = useRef<any>(null);
  const [isFocused, setIsFocused] = useState(false);
  const canSend = value.trim().length > 0 && !sending && !disabled;

  const handleKeyPress = (e: any) => {
    if (Platform.OS === 'web') {
      try {
        const key = e?.nativeEvent?.key || e?.key;
        const shift = e?.nativeEvent?.shiftKey || e?.shiftKey;
        if (key === 'Enter' && !shift) {
          if (typeof e.preventDefault === 'function') e.preventDefault();
          if (typeof e.stopPropagation === 'function') e.stopPropagation();
          if (canSend) onSend();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ChatInputBar] key handler', err);
      }
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.inputRow}>
        <View style={[styles.inputWrapper, isFocused && styles.inputWrapperFocused]}>
          <TextInput
            ref={inputRef}
            style={[styles.textInputInner, inputStyle]}
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor="#9CA3AF"
            multiline
            maxLength={maxLength}
            editable={!disabled}
            returnKeyType="send"
            onSubmitEditing={canSend ? onSend : undefined}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </View>

        <View style={styles.actionIcons}>
          <TouchableOpacity style={styles.iconButton} onPress={() => {
            if (typeof onMentionPress === 'function') return onMentionPress();
            // default: insert @ and focus
            try { inputRef.current?.focus(); onChangeText((value||'') + '@'); } catch (e) {}
          }}>
            <PlatformIcon name={'alternate_email'} size={20} color={'#6B7280'} family="MaterialIcons" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.iconButton}
            onPress={() => {
              if (typeof onEmojiPress === 'function') return onEmojiPress();
              // default: focus input (mobile will show emoji keyboard on some devices)
              try { inputRef.current?.focus(); } catch (e) {}
            }}
          >
            <PlatformIcon name={'insert_emoticon'} size={20} color={'#6B7280'} family="MaterialIcons" />
          </TouchableOpacity>
          {/* image icon removed per request */}

          <TouchableOpacity
            style={[
              styles.sendButton,
              Platform.OS !== 'web' ? { backgroundColor: themeColor } : styles.sendButtonWeb,
              sendButtonStyle,
              !canSend && styles.sendButtonDisabled,
            ]}
            onPress={() => { if (canSend) onSend(); }}
            disabled={!canSend}
            accessibilityLabel="Send"
          >
            {Platform.OS === 'web' ? (
              <PlatformIcon name={'send'} size={20} color={canSend ? themeColor : '#9CA3AF'} family="MaterialIcons" />
            ) : (
              <Text style={styles.sendButtonText}>{sending ? '...' : '發送'}</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inputWrapper: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    borderRadius: 999,
    paddingHorizontal: 22,
    paddingVertical: 0,
    minHeight: 40,
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 6,
  },
  inputWrapperFocused: {
    backgroundColor: '#F3F4F6',
  },
  textInputInner: {
    flex: 1,
    paddingVertical: 0,
    paddingHorizontal: 6,
    fontSize: 15,
    lineHeight: Platform.OS === 'web' ? 24 : 20,
    height: Platform.OS === 'web' ? 24 : undefined,
    backgroundColor: 'transparent',
    borderWidth: 0,
    outlineWidth: 0,
    outlineStyle: 'none',
    boxShadow: 'none',
    color: '#111827',
    textAlignVertical: 'center',
  },
  actionIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  sendButton: {
    minWidth: 44,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  sendButtonWeb: {
    backgroundColor: 'transparent',
  },
  sendButtonDisabled: {
    opacity: 0.45,
  },
  sendButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});
